import React, { createContext, useEffect, useState } from 'react';
import { usersAPI } from '../services/api';

export const FilterContext = createContext(null);

const DEFAULT_ADDRESS = process.env.REACT_APP_DEFAULT_SEARCH_ADDRESS || 'Bahnhofplatz 1, 8400 Winterthur';
const ADDRESS_STORAGE_KEY = 'winterthur_aktiv_search_address';

export const FilterProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const minutes = now.getMinutes();
    const roundedMinutes = minutes <= 30 ? 30 : 60;
    const roundedTime = new Date(now);
    roundedTime.setSeconds(0, 0);
    roundedTime.setMinutes(roundedMinutes);
    if (roundedMinutes === 60) {
      roundedTime.setHours(roundedTime.getHours() + 1);
      roundedTime.setMinutes(0);
    }
    const defaultTime = roundedTime.toTimeString().slice(0, 5);
    const savedAddress = localStorage.getItem(ADDRESS_STORAGE_KEY) || DEFAULT_ADDRESS;
    return {
      start_address: savedAddress,
      departure_date: today,
      departure_time: defaultTime,
      age_min: null,
      age_max: null,
      categories: [],
      indoor_outdoor: null,
      cost_range: null,
      duration: null,
      wheelchair_accessible: false,
      event_type: null,
    };
  });

  // Listen to user changes from AuthContext via window events
  useEffect(() => {
    const handleUserChange = (event) => {
      const userData = event.detail;
      setUser(userData);
      
      // Update start_address when user changes
      if (userData) {
        // Fallback: current_search_address → home_address → DEFAULT_ADDRESS
        const address = userData.current_search_address || userData.home_address || DEFAULT_ADDRESS;
        setFilters(prev => ({ ...prev, start_address: address }));
      } else {
        // User logged out, restore last saved address
        const savedAddress = localStorage.getItem(ADDRESS_STORAGE_KEY) || DEFAULT_ADDRESS;
        setFilters(prev => ({ ...prev, start_address: savedAddress }));
      }
    };

    window.addEventListener('auth-user-changed', handleUserChange);
    
    // Get initial user state
    const initialUser = window.__currentUser;
    if (initialUser) {
      setUser(initialUser);
      const address = initialUser.current_search_address || initialUser.home_address || DEFAULT_ADDRESS;
      setFilters(prev => ({ ...prev, start_address: address }));
    }

    return () => {
      window.removeEventListener('auth-user-changed', handleUserChange);
    };
  }, []);

  const updateFilter = async (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    
    // If start_address changed, persist locally and (if logged in) in the database
    if (key === 'start_address') {
      localStorage.setItem(ADDRESS_STORAGE_KEY, value);
      if (user) {
        try {
          await usersAPI.updateSettings({ current_search_address: value });
        } catch (error) {
          console.error('Failed to save search address:', error);
        }
      }
    }
  };

  const resetFilters = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const minutes = now.getMinutes();
    const roundedMinutes = minutes <= 30 ? 30 : 60;
    const roundedTime = new Date(now);
    roundedTime.setSeconds(0, 0);
    roundedTime.setMinutes(roundedMinutes);
    if (roundedMinutes === 60) {
      roundedTime.setHours(roundedTime.getHours() + 1);
      roundedTime.setMinutes(0);
    }
    const defaultTime = roundedTime.toTimeString().slice(0, 5);
    
    const defaultAddress = user?.current_search_address || user?.home_address || DEFAULT_ADDRESS;
    
    setFilters({
      start_address: defaultAddress,
      departure_date: today,
      departure_time: defaultTime,
      age_min: null,
      age_max: null,
      categories: [],
      indoor_outdoor: null,
      cost_range: null,
      duration: null,
      wheelchair_accessible: false,
      event_type: null,
    });
  };

  const value = {
    filters,
    updateFilter,
    resetFilters,
  };

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};
